import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
const SESSION_KEY = "hawkam_session";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=admin
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Access token is httpOnly, so we track a lightweight marker in localStorage
    // to avoid noisy 401s on every public page for anonymous visitors.
    if (typeof window === "undefined" || !localStorage.getItem(SESSION_KEY)) {
      setUser(false);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        localStorage.removeItem(SESSION_KEY);
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(SESSION_KEY, "1");
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    localStorage.removeItem(SESSION_KEY);
    setUser(false);
  };

  const refreshMe = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshMe, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
